import { useEffect, useMemo, useState } from 'react';
import { fetchLlmCatalogApi } from '../api/llm.api';
import { FALLBACK_PICKER_MODELS, toPickerModels } from '../constants/chatImageModels';
import { useAppSelector } from './useAppRedux';
import { getAuthToken } from '../utils/storage';

let cachedCatalog = null;
let cachedAuthKey = null;
let inflight = null;

const authCacheKey = () => (getAuthToken() ? 'auth' : 'anon');

const loadCatalog = (authKey) => {
  if (cachedCatalog && cachedAuthKey === authKey) {
    return Promise.resolve(cachedCatalog);
  }

  if (!inflight) {
    inflight = fetchLlmCatalogApi()
      .then((items) => {
        cachedCatalog = Array.isArray(items) ? items : [];
        cachedAuthKey = authKey;
        return cachedCatalog;
      })
      .catch(() => [])
      .finally(() => {
        inflight = null;
      });
  }

  return inflight;
};

export const useLlmCatalog = () => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const authKey = isAuthenticated ? 'auth' : 'anon';
  const [raw, setRaw] = useState(cachedAuthKey === authKey ? cachedCatalog : null);
  const [loading, setLoading] = useState(!(cachedCatalog && cachedAuthKey === authKey));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCatalog(authCacheKey()).then((items) => {
      if (!cancelled) {
        setRaw(items);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authKey]);

  const models = useMemo(() => {
    if (Array.isArray(raw) && raw.length > 0) {
      return toPickerModels(raw);
    }
    return FALLBACK_PICKER_MODELS;
  }, [raw]);

  return { models, loading, fromApi: Array.isArray(raw) && raw.length > 0 };
};
